/**
 * EmbedPage — /embed (round9/embed-config). Everything about what a
 * builder's embedded designer OFFERS a homeowner, on one page with one Save:
 *
 *   1. Designer preset  — moved here verbatim from SettingsPage.tsx
 *                         (round7/d3-scope-config-portal); Rate Card keeps a
 *                         pointer.
 *   2. Designer tools   — per-tool on/off (boat slips, lifts, gangways,
 *                         seawall, railings, gates, roofs, sundecks, stairs).
 *   3. Design options   — which variant of each tool is offered (pier vs
 *                         floating, slip style, gate leaves, stair shape …).
 *   4. Materials & finishes — decking, pilings, rail/roof colours, roof style.
 *   5. Boat sizes       — which stock sizes are offered, plus the builder's
 *                         own custom slip sizes.
 *
 * Role model — same as SettingsPage.tsx's Embed configuration section: the
 * whole PUT /api/portal/tenant endpoint is owner-only server-side, so a `rep`
 * sees read-only summaries instead of controls (auth.ts decodes
 * `custom:role`). No client-side TIER gate anywhere: a preset the tenant's
 * tier disallows is caught server-side (422 PRESET_NOT_ALLOWED_FOR_TIER) and
 * that message is surfaced verbatim. The one entitlement-derived signal used
 * here is `tenant.entitlements.editScope` (server-resolved) to say — in
 * words, next to a disabled checkbox — that a tool's whole section is not
 * available under the current preset; the backend clamps stored toggles to
 * the resolved scope on /api/config regardless of what is sent.
 *
 * Draft ↔ wire logic (and the backend's whole-field / omit-when-all-checked
 * / append-only semantics it encodes) lives in lib/embedConfig.ts so it is
 * unit-testable without React; this file is layout + state + the request.
 * Errors: StatusMessage only; backend codes with specific messages surfaced
 * verbatim (EMBED_CONFIG_ERROR_CODES), everything else via
 * friendlyErrorMessage — the same convention SettingsPage.tsx uses.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import {
  getTenant, updateTenant, ApiError,
  type TenantConfig, type UpdateTenantPatch, type DesignerPresetName, type CustomBoatBay,
 listTemplates } from "../lib/api";
import {
  DESIGNER_PRESET_OPTIONS, TOOL_GROUPS, DESIGN_OPTION_CATEGORIES, FINISH_CATEGORIES, BOAT_SIZE_CATEGORY,
  categoryLabel, categoryOptions,
  toolDraftFromTenant, toolOptionsFromDraft, toolAllowedByScope,
  optionDraftFromTenant, catalogOverridesFromDraft,
  customBaysFromDrafts, emptyNewBayDraft, describeCustomBoatBay,
  describeToolOptions, describeCatalogOverrides,
  EMBED_CONFIG_ERROR_CODES, CUSTOM_BAY_LIMITS, MAX_CUSTOM_BAYS,
  type ToolDraft, type OptionDraft, type NewBayDraft, type OptionId,
} from "../lib/embedConfig";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import { SettingsLayout } from "../components/shell/SettingsRail";
import { templateConflicts, hasConflicts } from "../lib/templateConflicts";
import ui from "../styles/ui.module.css";
import styles from "./EmbedPage.module.css";

export function EmbedPage() {
  const { user, signOut } = useAuth();
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [designerPresetDraft, setDesignerPresetDraft] = useState<DesignerPresetName | "">("");
  const [toolDraft, setToolDraft] = useState<ToolDraft>(() => toolDraftFromTenant(undefined));
  const [optionDraft, setOptionDraft] = useState<OptionDraft>(() => optionDraftFromTenant(undefined));
  // Stored custom sizes (label/short editable; key + dimensions fixed) and
  // unsaved new rows (fully editable until saved).
  const [storedBays, setStoredBays] = useState<CustomBoatBay[]>([]);
  const [newBays, setNewBays] = useState<NewBayDraft[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const applyTenantToForm = useCallback((result: TenantConfig) => {
    setTenant(result);
    setDesignerPresetDraft(result.designerPreset ?? "");
    setToolDraft(toolDraftFromTenant(result.toolOptions));
    const bays = Array.isArray(result.customBoatBays) ? result.customBoatBays.map((b) => ({ ...b })) : [];
    setStoredBays(bays);
    setOptionDraft(optionDraftFromTenant(result.catalogOverrides, bays));
    setNewBays([]);
  }, []);

  // Same promise-chain shape SettingsPage.tsx uses for its tenant load (the
  // repo's react-hooks lint rejects a synchronous setState — including one
  // reached through a helper — inside an effect; state here is only touched
  // in the async continuations). The `cancelled` guard covers an unmount
  // mid-flight; the user-triggered retry below needs neither.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getTenant(user.idToken)
      .then((result) => { if (!cancelled) applyTenantToForm(result); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setLoadError(friendlyErrorMessage(err, "loading your embed configuration"));
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [user, signOut, applyTenantToForm]);

  const handleRetryLoad = useCallback(() => {
    if (!user) return;
    setLoadError(null);
    setIsLoading(true);
    getTenant(user.idToken)
      .then((result) => { applyTenantToForm(result); })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setLoadError(friendlyErrorMessage(err, "loading your embed configuration"));
      })
      .finally(() => setIsLoading(false));
  }, [user, signOut, applyTenantToForm]);

  const toggleOption = (category: OptionDraft extends Record<infer K, unknown> ? K : never, key: OptionId, checked: boolean) => {
    setOptionDraft((d) => {
      const current = d[category] ?? [];
      const next = checked
        ? (current.includes(key) ? current : [...current, key])
        : current.filter((k) => k !== key);
      return { ...d, [category]: next };
    });
  };

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    if (!user || !tenant) return;

    const bays = customBaysFromDrafts(storedBays, newBays);
    if (!bays.ok) { setSaveError(bays.error); return; }
    const allBays = bays.value;
    // A size added this save is offered by default: fold its (freshly minted)
    // key into the Boat sizes allow-list before computing the wire value.
    const newKeys = allBays.slice(storedBays.length).map((b) => b.key);
    const draftForWire: OptionDraft = {
      ...optionDraft,
      [BOAT_SIZE_CATEGORY]: [...(optionDraft[BOAT_SIZE_CATEGORY] ?? []), ...newKeys],
    };
    const catalog = catalogOverridesFromDraft(draftForWire, allBays);
    if (!catalog.ok) { setSaveError(catalog.error); return; }

    const patch: UpdateTenantPatch = {
      toolOptions: toolOptionsFromDraft(toolDraft),
      catalogOverrides: catalog.value,
    };
    // customBoatBays is append-only server-side and `null` is rejected, so it
    // is only sent when there is a list to send.
    if (allBays.length > 0) patch.customBoatBays = allBays;
    // Same "omit rather than send an empty sentinel" posture SettingsPage
    // used for this field — there is no "unset" value the backend accepts.
    if (designerPresetDraft) patch.designerPreset = designerPresetDraft;

    setIsSaving(true);
    try {
      const updated = await updateTenant(user.idToken, patch);
      // Re-derive every draft from the echo: newly added sizes become stored
      // (fixed) rows, and a cleared catalogOverrides comes back as absent.
      applyTenantToForm(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      // round10/debt-sweep (DEBT round9/embed-config #2): a saved template can
      // now use a tool or option homeowners can no longer choose, and every
      // homeowner starting from it would hit TOOL_DISABLED / CATALOG_VIOLATION
      // at submit. Say so here, once, right after the save that caused it.
      // Best-effort: the templates read is a separate endpoint and a failure
      // there must not turn a successful save into an error.
      listTemplates(user.idToken)
        .then((templates) => {
          const conflicting = templates.filter((t) => hasConflicts(templateConflicts(t.designState, updated)));
          setConflictNotice(conflicting.length === 0 ? null
            : conflicting.length === 1
              ? `“${conflicting[0].name}” now uses something homeowners can’t choose. Review it on the Templates page.`
              : `${conflicting.length} templates now use something homeowners can’t choose. Review them on the Templates page.`);
        })
        .catch(() => setConflictNotice(null));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      if (err instanceof ApiError && EMBED_CONFIG_ERROR_CODES.has(err.code)) {
        setSaveError(err.message);
        return;
      }
      setSaveError(friendlyErrorMessage(err, "saving your embed configuration"));
    } finally {
      setIsSaving(false);
    }
  }

  if (!user) return null;

  if (isLoading) {
    return (
      <SettingsLayout active="embed">
        <main id="main-content" className={ui.main}>
          <div className={ui.pageHeader}>
            <div>
              <h1>Embed</h1>
              <p className={ui.pageHint}>What your embedded designer offers homeowners.</p>
            </div>
          </div>
          <p className={ui.loading}>Loading embed configuration…</p>
        </main>
      </SettingsLayout>
    );
  }

  if (loadError && !tenant) {
    return (
      <SettingsLayout active="embed">
        <main id="main-content" className={ui.main}>
          <div className={ui.errorBox}>
            <h1>Embed</h1>
            <StatusMessage variant="error" message={loadError} className={ui.error} />
            <div className={ui.actions}>
              <button type="button" onClick={handleRetryLoad} className={ui.button}>Retry</button>
              <Link to="/leads" className={ui.buttonSecondary}>Back to leads</Link>
            </div>
          </div>
        </main>
      </SettingsLayout>
    );
  }

  if (!tenant) return null;

  const isOwner = user.role === "owner";
  const editScope = tenant.entitlements?.editScope;
  const currentPresetOption = tenant.designerPreset
    ? DESIGNER_PRESET_OPTIONS.find((o) => o.value === tenant.designerPreset)
    : undefined;
  const boatSizeOptions = categoryOptions(BOAT_SIZE_CATEGORY, storedBays);

  const renderCategory = (category: typeof DESIGN_OPTION_CATEGORIES[number], customBays?: CustomBoatBay[]) => (
    <fieldset className={styles.groupFieldset} key={category}>
      <legend className={styles.groupLegend}>{categoryLabel(category)}</legend>
      <div className={styles.optionGrid}>
        {categoryOptions(category, customBays).map(({ key, label }) => (
          <label className={styles.checkboxField} key={String(key)}>
            <input
              type="checkbox"
              name={`opt:${category}`}
              value={String(key)}
              checked={(optionDraft[category] ?? []).includes(key)}
              onChange={(e) => toggleOption(category, key, e.target.checked)}
            />
            <span className={styles.checkboxBody}>
              <span className={styles.checkboxLabel}>{label}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );

  return (
    <SettingsLayout active="embed">
      <main id="main-content" className={ui.main}>
        <form className={styles.form} onSubmit={handleSave}>
          <div className={ui.pageHeader}>
            <div>
              <h1>Embed</h1>
              <p className={ui.pageHint}>
                Choose what your embedded designer offers homeowners. Changes reach your
                live embed within about 5 minutes — its public configuration is cached for
                up to 300 seconds.
              </p>
            </div>
          </div>

          {/* 1. Designer preset — moved from Rate Card (round7/d3-scope-config-portal). */}
          <section className={ui.card}>
            <h2 className={ui.cardTitle}>Designer preset</h2>
            <p className={ui.cardHint}>
              The overall shape of the design tool — this controls what a homeowner can
              do, independent of your plan. The tools and options below narrow it further.
            </p>
            {isOwner ? (
              <>
                <fieldset className={ui.fieldset}>
                  <legend className={ui.legend}>Designer preset</legend>
                  <div className={ui.radioOptions}>
                    {DESIGNER_PRESET_OPTIONS.map(({ value, label, description }) => (
                      <label className={ui.radioOption} key={value}>
                        <input
                          type="radio"
                          name="designerPreset"
                          value={value}
                          checked={designerPresetDraft === value}
                          onChange={() => setDesignerPresetDraft(value)}
                        />
                        <span className={ui.radioOptionBody}>
                          <span className={ui.radioOptionLabel}>{label}</span>
                          <span className={ui.radioOptionDesc}>{description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                {/* No client-side tier gate — see the file header. */}
                <p className={ui.cardHint}>
                  Your plan may not allow every preset above — saving will explain why if
                  you pick one it doesn&rsquo;t.
                </p>
              </>
            ) : (
              <p className={ui.subtle}>
                Current: <strong>{currentPresetOption ? currentPresetOption.label : "Not set"}</strong>
                {currentPresetOption ? ` — ${currentPresetOption.description}` : (
                  " — your embed uses your plan's default access until an owner selects a preset."
                )}
                {" "}Only the account owner can change this.
              </p>
            )}
          </section>

          {/* 2. Designer tools */}
          <section className={ui.card}>
            <h2 className={ui.cardTitle}>Designer tools</h2>
            <p className={ui.cardHint}>
              Turn off any tool you don&rsquo;t build. Homeowners won&rsquo;t see it, and a
              design that uses it can&rsquo;t be submitted.
            </p>
            {isOwner ? (
              TOOL_GROUPS.map((group) => (
                <fieldset className={styles.groupFieldset} key={group.scope}>
                  <legend className={styles.groupLegend}>{group.scopeLabel}</legend>
                  <div className={styles.toolGrid}>
                    {group.tools.map((tool) => {
                      const allowed = toolAllowedByScope(editScope, tool.scope);
                      const descId = `tool-desc-${tool.key}`;
                      return (
                        <label
                          className={`${styles.checkboxField}${allowed ? "" : ` ${styles.checkboxDisabled}`}`}
                          key={tool.key}
                        >
                          <input
                            type="checkbox"
                            name={`tool:${tool.key}`}
                            checked={toolDraft[tool.key]}
                            disabled={!allowed}
                            aria-describedby={descId}
                            onChange={(e) => setToolDraft((d) => ({ ...d, [tool.key]: e.target.checked }))}
                          />
                          <span className={styles.checkboxBody}>
                            <span className={styles.checkboxLabel}>{tool.label}</span>
                            <span id={descId} className={styles.checkboxDesc}>
                              {tool.description}
                              {!allowed && " Not available under your current designer preset."}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ))
            ) : (
              <ul className={styles.summaryList}>
                {describeToolOptions(tenant.toolOptions, editScope).map((line) => <li key={line}>{line}</li>)}
                <li>Only the account owner can change this.</li>
              </ul>
            )}
          </section>

          {/* 3. Design options (variants) */}
          <section className={ui.card}>
            <h2 className={ui.cardTitle}>Design options</h2>
            <p className={ui.cardHint}>
              Which variants of each tool homeowners can choose. Keep at least one per row;
              a row with a single choice is applied automatically and hidden in the designer.
            </p>
            {isOwner ? (
              DESIGN_OPTION_CATEGORIES.map((category) => renderCategory(category))
            ) : (
              <ul className={styles.summaryList}>
                {describeCatalogOverrides(
                  tenant.catalogOverrides
                    ? Object.fromEntries(Object.entries(tenant.catalogOverrides).filter(([k]) => (DESIGN_OPTION_CATEGORIES as string[]).includes(k)))
                    : undefined,
                ).map((line) => <li key={line}>{line}</li>)}
              </ul>
            )}
          </section>

          {/* 4. Materials & finishes */}
          <section className={ui.card}>
            <h2 className={ui.cardTitle}>Materials &amp; finishes</h2>
            <p className={ui.cardHint}>
              Decking, pilings, rail colours, roof styles and roof colours homeowners can
              pick. A design already using something you turn off is switched to your
              first offered choice when it loads.
            </p>
            {isOwner ? (
              FINISH_CATEGORIES.map((category) => renderCategory(category))
            ) : (
              <ul className={styles.summaryList}>
                {describeCatalogOverrides(
                  tenant.catalogOverrides
                    ? Object.fromEntries(Object.entries(tenant.catalogOverrides).filter(([k]) => (FINISH_CATEGORIES as string[]).includes(k)))
                    : undefined,
                ).map((line) => <li key={line}>{line}</li>)}
              </ul>
            )}
          </section>

          {/* 5. Boat sizes — stock + custom */}
          <section className={ui.card}>
            <h2 className={ui.cardTitle}>Boat sizes</h2>
            <p className={ui.cardHint}>
              Which slip sizes homeowners can place. Add your own sizes below — a size
              you add is offered right away. Once saved, a custom size&rsquo;s dimensions
              can&rsquo;t change and it can&rsquo;t be deleted (saved designs may use it);
              untick it here to stop offering it.
            </p>
            {isOwner ? (
              <>
                <fieldset className={styles.groupFieldset}>
                  <legend className={styles.groupLegend}>Boat sizes offered</legend>
                  <div className={styles.optionGrid}>
                    {boatSizeOptions.map(({ key, label }) => (
                      <label className={styles.checkboxField} key={String(key)}>
                        <input
                          type="checkbox"
                          name={`opt:${BOAT_SIZE_CATEGORY}`}
                          value={String(key)}
                          checked={(optionDraft[BOAT_SIZE_CATEGORY] ?? []).includes(key)}
                          onChange={(e) => toggleOption(BOAT_SIZE_CATEGORY, key, e.target.checked)}
                        />
                        <span className={styles.checkboxBody}>
                          <span className={styles.checkboxLabel}>{label}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset className={styles.groupFieldset}>
                  <legend className={styles.groupLegend}>Your custom sizes</legend>
                  {storedBays.length === 0 && newBays.length === 0 && (
                    <p className={ui.subtle}>None yet.</p>
                  )}
                  <div className={styles.bayRows}>
                    {storedBays.map((bay, idx) => (
                      <div className={styles.bayRow} key={bay.key} data-testid="stored-bay-row">
                        <label className={styles.bayField}>
                          <span>Name</span>
                          <input
                            type="text"
                            name={`bay:${bay.key}:label`}
                            value={bay.label}
                            maxLength={CUSTOM_BAY_LIMITS.labelMax}
                            onChange={(e) => setStoredBays((rows) => rows.map((r, i) => i === idx ? { ...r, label: e.target.value } : r))}
                          />
                        </label>
                        <label className={styles.bayField}>
                          <span>Short name</span>
                          <input
                            type="text"
                            name={`bay:${bay.key}:short`}
                            value={bay.short ?? ""}
                            maxLength={CUSTOM_BAY_LIMITS.shortMax}
                            onChange={(e) => setStoredBays((rows) => rows.map((r, i) => i === idx ? { ...r, short: e.target.value } : r))}
                          />
                        </label>
                        <span className={styles.bayDims}>{bay.beamFt}′ beam</span>
                        <span className={styles.bayDims}>{bay.lengthFt}′ long</span>
                        <span className={styles.bayDims}>{bay.slipDepthFt}′ deep</span>
                        <span className={styles.bayDims} aria-hidden="true" />
                      </div>
                    ))}
                    {newBays.map((row, idx) => (
                      <div className={styles.bayRow} key={row.id} data-testid="new-bay-row">
                        <label className={styles.bayField}>
                          <span>Name</span>
                          <input
                            type="text"
                            name={`newbay:${idx}:label`}
                            value={row.label}
                            maxLength={CUSTOM_BAY_LIMITS.labelMax}
                            placeholder="e.g. Pontoon 26"
                            onChange={(e) => setNewBays((rows) => rows.map((r) => r.id === row.id ? { ...r, label: e.target.value } : r))}
                          />
                        </label>
                        <label className={styles.bayField}>
                          <span>Short name</span>
                          <input
                            type="text"
                            name={`newbay:${idx}:short`}
                            value={row.short}
                            maxLength={CUSTOM_BAY_LIMITS.shortMax}
                            placeholder="optional"
                            onChange={(e) => setNewBays((rows) => rows.map((r) => r.id === row.id ? { ...r, short: e.target.value } : r))}
                          />
                        </label>
                        <label className={styles.bayField}>
                          <span>Beam (ft)</span>
                          <input
                            type="number" inputMode="decimal" step="0.5"
                            min={CUSTOM_BAY_LIMITS.beamFt.min} max={CUSTOM_BAY_LIMITS.beamFt.max}
                            name={`newbay:${idx}:beamFt`}
                            value={row.beamFt}
                            onChange={(e) => setNewBays((rows) => rows.map((r) => r.id === row.id ? { ...r, beamFt: e.target.value } : r))}
                          />
                        </label>
                        <label className={styles.bayField}>
                          <span>Length (ft)</span>
                          <input
                            type="number" inputMode="decimal" step="0.5"
                            min={CUSTOM_BAY_LIMITS.lengthFt.min} max={CUSTOM_BAY_LIMITS.lengthFt.max}
                            name={`newbay:${idx}:lengthFt`}
                            value={row.lengthFt}
                            onChange={(e) => setNewBays((rows) => rows.map((r) => r.id === row.id ? { ...r, lengthFt: e.target.value } : r))}
                          />
                        </label>
                        <label className={styles.bayField}>
                          <span>Slip depth (ft)</span>
                          <input
                            type="number" inputMode="decimal" step="0.5"
                            min={CUSTOM_BAY_LIMITS.slipDepthFt.min} max={CUSTOM_BAY_LIMITS.slipDepthFt.max}
                            name={`newbay:${idx}:slipDepthFt`}
                            value={row.slipDepthFt}
                            onChange={(e) => setNewBays((rows) => rows.map((r) => r.id === row.id ? { ...r, slipDepthFt: e.target.value } : r))}
                          />
                        </label>
                        <button
                          type="button"
                          className={ui.iconButton}
                          aria-label={`Remove unsaved size ${row.label || idx + 1}`}
                          onClick={() => setNewBays((rows) => rows.filter((r) => r.id !== row.id))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  {storedBays.length + newBays.length < MAX_CUSTOM_BAYS && (
                    <button
                      type="button"
                      className={`${ui.buttonGhost} ${ui.buttonSmall}`}
                      onClick={() => setNewBays((rows) => [...rows, emptyNewBayDraft()])}
                    >
                      + Add custom size
                    </button>
                  )}
                </fieldset>
              </>
            ) : (
              <ul className={styles.summaryList}>
                {describeCatalogOverrides(
                  tenant.catalogOverrides && tenant.catalogOverrides[BOAT_SIZE_CATEGORY]
                    ? { [BOAT_SIZE_CATEGORY]: tenant.catalogOverrides[BOAT_SIZE_CATEGORY] }
                    : undefined,
                  storedBays,
                ).map((line) => <li key={line}>{line}</li>)}
                {storedBays.map((b) => <li key={b.key}>{describeCustomBoatBay(b)}</li>)}
              </ul>
            )}
          </section>

          <div className={ui.saveBar}>
            <button type="submit" className={ui.button} disabled={isSaving || !isOwner}>
              {isSaving ? "Saving…" : "Save"}
            </button>
            {!isOwner && <span className={ui.subtle}>Only the account owner can change these settings.</span>}
            {saved && <StatusMessage variant="status" message="Saved." className={ui.savedInline} />}
            {saveError && <StatusMessage variant="error" message={saveError} className={ui.error} />}
          </div>
          {conflictNotice && (
            <StatusMessage variant="status" message={conflictNotice} className={ui.warning}>
              {" "}<Link to="/templates">Open Templates</Link>
            </StatusMessage>
          )}
        </form>
      </main>
    </SettingsLayout>
  );
}
